import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Row, Col, Button, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import dataServices from "../../apiServices/data.services";
import { useAlert } from "react-alert";
import Loader from "../../components/Loader/Loader";
import { Can } from "../../context/AuthProvider";
import { handleValidationError } from "../../components/CommonFunctions";

const EditSubscription = () => {
  const { id } = useParams();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const alert = useAlert();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    Promise.all([dataServices.getSubscriptionDetails(id), dataServices.getPlans()])
      .then(([subRes, plansRes]) => {
        if (subRes.status === 200) {
          setSubscription(subRes.data);
        }
        if (plansRes.status === 200) {
          setPlans(plansRes.data);
        }
      })
      .catch((err) => {
        alert.error(handleValidationError(err));
      })
      .finally(() => setLoading(false));
  }, [id, alert]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      plan_id: subscription?.plan_id || subscription?.plan?.id || "",
      start_date: subscription?.start_date || "",
      end_date: subscription?.end_date || "",
      status: subscription?.status || "active",
      description: subscription?.description || "",
    },
    validationSchema: Yup.object({
      plan_id: Yup.string().required("Plan is required"),
      start_date: Yup.string().required("Start date is required"),
      status: Yup.string().required("Status is required"),
    }),
    onSubmit: (values) => {
      setLoading(true);
      dataServices
        .editSubscription(id, values)
        .then((res) => {
          if (res.status === 200) {
            alert.success("Subscription updated successfully");
            navigate("/subscriptions");
          }
        })
        .catch((err) => {
          alert.error(handleValidationError(err));
        })
        .finally(() => setLoading(false));
    },
  });

  if (!subscription) {
    return <Loader />;
  }

  return (
    <div className="content-wrapper">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-2 pb-1 fw-bold text-black">Edit Subscription</h6>
      </div>
      <Can I="edit" this="Subscription">
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col sm={6}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="plan_id">Plan <span className="text-danger">*</span></Form.Label>
                <Form.Control as="select" id="plan_id" name="plan_id" value={formik.values.plan_id} onChange={formik.handleChange}>
                  <option value="">Select plan</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name}
                    </option>
                  ))}
                </Form.Control>
                {formik.touched.plan_id && formik.errors.plan_id ? <div className="invalid-feedback d-block">{formik.errors.plan_id}</div> : null}
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="start_date">Start Date <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  id="start_date"
                  name="start_date"
                  type="date"
                  value={formik.values.start_date}
                  onChange={formik.handleChange}
                  className={formik.touched.start_date && formik.errors.start_date ? "form-control is-invalid" : "form-control"}
                />
                {formik.touched.start_date && formik.errors.start_date ? <div className="invalid-feedback">{formik.errors.start_date}</div> : null}
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="end_date">End Date</Form.Label>
                <Form.Control id="end_date" name="end_date" type="date" value={formik.values.end_date} onChange={formik.handleChange} />
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="status">Status <span className="text-danger">*</span></Form.Label>
                <Form.Control as="select" id="status" name="status" value={formik.values.status} onChange={formik.handleChange}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col sm={12}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="description">Description</Form.Label>
                <Form.Control
                  as="textarea"
                  id="description"
                  name="description"
                  rows={3}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button type="button" className="btn btn-secondary w-100" onClick={() => navigate("/subscriptions")}>Cancel</Button>
            </Col>
            <Col className="footer mt-auto">
              <Button type="submit" disabled={loading} className="btn btn-primary w-100">Save Changes</Button>
            </Col>
          </Row>
        </Form>
      </Can>
    </div>
  );
};

export default EditSubscription;
